"use client";

import clsx from "clsx";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Stepper from "@/components/common/wizard/Stepper";
import Button from "@/components/library/Button";
import Icon, {type IconNameExtended} from "@/components/library/Icon";
import Input from "@/components/library/Input";
import SegmentedControl from "@/components/library/SegmentedControl";
import {InferVoiceProviderFromVoiceId} from "@/enums/agencies";
import {Permissions, RoleData} from "@/enums/user";
import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import {useVerifyScriptVoice} from "@/hooks/useVerifyScriptVoice";
import type {ScriptDraft, ScriptDraftUseCase} from "@/services/wingman";
import {DEFAULT_VOICE_ID} from "@/utils/agencies/voices";
import ScriptDraftVoiceSection from "./ScriptDraftVoiceSection";
import {renderDraftContentWithVariables} from "./draftVariables";
import {
   getDraftDirection,
   type ScriptDraftDirection,
   type ScriptDraftVoice,
} from "./scriptDraftSave";
import {useScriptDraftSave} from "./useScriptDraftSave";

/**
 * Speech bubble for a single spoken line (greeting / goodbye / voicemail).
 * Mirrors the read-only bubble styling of the script preview page so drafts
 * feel like the script they will become.
 */
const SpeechBubble: React.FC<{
   content: string;
   label: string;
   icon: IconNameExtended;
   colorClass: string;
}> = ({content, label, icon, colorClass}) => (
   <div className="space-y-2">
      <div className="flex items-center gap-2">
         <div className={clsx("w-8 h-8 rounded-full flex items-center justify-center", colorClass)}>
            <Icon icon={icon} className="w-4 h-4 text-white" />
         </div>
         <span className="text-sm font-medium text-700">{label}</span>
      </div>
      <div className={clsx("relative ml-4 p-4 rounded-2xl rounded-tl-sm", "bg-100 dark:bg-800 border border-200")}>
         <p className="text-sm text-foreground whitespace-pre-wrap">
            {renderDraftContentWithVariables(content)}
         </p>
      </div>
   </div>
);

const UseCaseCard: React.FC<{useCase: ScriptDraftUseCase; index: number}> = ({useCase, index}) => {
   const {t} = useTranslation("ask");

   return (
      <div className="rounded-xl border border-200 bg-panel p-4 space-y-3">
         <div className="flex items-start gap-3">
            <span
               className={clsx(
                  "flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 mt-0.5",
                  "bg-primary/10 text-primary text-xs font-semibold"
               )}
            >
               {index + 1}
            </span>
            <div className="min-w-0 space-y-1">
               <p className="text-sm font-medium text-900">{useCase.title}</p>
               <p className="text-sm text-700">
                  {renderDraftContentWithVariables(useCase.description)}
               </p>
            </div>
         </div>
         {useCase.actions.length > 0 && (
            <div className="ml-9 space-y-2">
               <p className="text-xs font-medium text-500 uppercase tracking-wide">
                  {t("workspace.scriptDraft.steps")}
               </p>
               <ul className="space-y-1.5">
                  {useCase.actions.map((action, actionIndex) => (
                     // Actions are ordered, append-only steps without ids — index is the identity.
                     <li key={actionIndex} className="flex items-start gap-2 text-sm text-700">
                        <Icon icon="arrow-right" className="w-3 h-3 text-primary mt-1 flex-shrink-0" />
                        <span>{renderDraftContentWithVariables(action.description)}</span>
                     </li>
                  ))}
               </ul>
            </div>
         )}
      </div>
   );
};

/**
 * The agent's name as part of the draft itself (mirrors the script editor's
 * Agent section): how the agent introduces itself on calls. Required by the
 * backend on save, so it's edited here rather than at the save action.
 */
const AgentNameSection: React.FC<{value: string; onChange: (value: string) => void}> = ({
   value,
   onChange,
}) => {
   const {t} = useTranslation("ask");

   return (
      <div className="space-y-2">
         <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-violet-500">
               <Icon icon="user-headset" className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-700">
               {t("workspace.scriptDraft.agentNameLabel")}
            </span>
         </div>
         <div className="ml-4">
            <Input
               type="text"
               value={value}
               onChange={(e) => onChange(e.target.value)}
               placeholder={t("workspace.scriptDraft.agentNamePlaceholder")}
               helperText={t("workspace.scriptDraft.agentNameHint")}
               aria-label={t("workspace.scriptDraft.agentNameLabel")}
            />
         </div>
      </div>
   );
};

/**
 * Whether the script answers calls (inbound) or is used when Gail calls out
 * (outbound). The draft's context rarely states it for new scripts, so the
 * user confirms here — it decides which scripts list the save lands in.
 */
const DirectionSection: React.FC<{
   value: ScriptDraftDirection;
   onChange: (value: ScriptDraftDirection) => void;
}> = ({value, onChange}) => {
   const {t} = useTranslation("ask");

   return (
      <div className="space-y-2">
         <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-sky-500">
               <Icon icon="phone" className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-700">
               {t("workspace.scriptDraft.directionLabel")}
            </span>
         </div>
         <div className="ml-4 space-y-1.5">
            <SegmentedControl<ScriptDraftDirection>
               ariaLabel={t("workspace.scriptDraft.directionLabel")}
               value={value}
               onChange={onChange}
               options={[
                  {
                     key: "inbound",
                     label: t("workspace.scriptDraft.directionInbound"),
                     icon: "phone-arrow-down-left",
                  },
                  {
                     key: "outbound",
                     label: t("workspace.scriptDraft.directionOutbound"),
                     icon: "phone-arrow-up-right",
                  },
               ]}
            />
            <p className="text-xs text-500">{t("workspace.scriptDraft.directionHint")}</p>
         </div>
      </div>
   );
};

/**
 * The two steps the draft is reviewed in, mirroring the script editor's
 * Script and Agent pages: content first, then who says it.
 */
type DraftStep = "script" | "agent";

/**
 * Footer for the draft wizard: step navigation plus the save action and its
 * outcome states. Stays mounted across steps so save state survives
 * navigation, and sticks to the bottom of the workspace scroll area so the
 * action stays reachable on long drafts.
 */
const ScriptDraftSaveSection: React.FC<{
   draft: ScriptDraft;
   artifactId: string;
   agentName: string;
   direction: ScriptDraftDirection;
   voice: ScriptDraftVoice;
   step: DraftStep;
   onStepChange: (step: DraftStep) => void;
   isVoiceAllowed?: boolean | null;
   onBlockedMissingVoice?: () => void;
}> = ({
   draft,
   artifactId,
   agentName,
   direction,
   voice,
   step,
   onStepChange,
   isVoiceAllowed = null,
   onBlockedMissingVoice,
}) => {
   const {t} = useTranslation("ask");
   const {t: tCommon} = useTranslation("common");
   const {state, save, unknownVariables, requestVariableFix, fixRequested} = useScriptDraftSave(
      draft,
      artifactId,
      true
   );

   if (state.status === "saved") {
      return (
         <div className="sticky bottom-2 pt-2">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/60 p-4 flex items-center gap-3 shadow-lg">
               <Icon icon="circle-check" className="w-5 h-5 text-emerald-600 flex-shrink-0" />
               <p className="flex-1 text-sm text-900 min-w-0 truncate">
                  {t("workspace.scriptDraft.savedAs", {name: draft.scriptName})}
               </p>
               <Button type="secondary" compact href={state.detailHref}>
                  {t("workspace.scriptDraft.viewScript")}
               </Button>
            </div>
         </div>
      );
   }

   return (
      <div className="sticky bottom-2 pt-2 space-y-2">
         {unknownVariables.length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-50 dark:bg-amber-950/60 p-3 space-y-1.5 shadow-lg">
               <div className="flex items-center gap-2">
                  <Icon
                     icon="triangle-exclamation"
                     className="w-4 h-4 text-amber-600 flex-shrink-0"
                  />
                  <p className="text-xs font-medium text-900">
                     {t("workspace.scriptDraft.unknownVariables")}
                  </p>
               </div>
               <div className="flex flex-wrap gap-1.5">
                  {unknownVariables.map((name) => (
                     <code
                        key={name}
                        className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs"
                     >
                        {`{${name}}`}
                     </code>
                  ))}
               </div>
               <p className="text-xs text-700">{t("workspace.scriptDraft.unknownVariablesHint")}</p>
               <div className="flex justify-end pt-1">
                  <Button
                     type="secondary"
                     compact
                     onClick={requestVariableFix}
                     disabled={fixRequested}
                     iconBefore="wand-magic-sparkles"
                  >
                     {fixRequested
                        ? t("workspace.scriptDraft.fixVariablesRequested")
                        : t("workspace.scriptDraft.fixVariables")}
                  </Button>
               </div>
            </div>
         )}

         {state.status === "error" && (
            <div className="rounded-xl border border-red-500/30 bg-red-50 dark:bg-red-950/60 p-3 flex items-start gap-2 shadow-lg">
               <Icon
                  icon="triangle-exclamation"
                  className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
               />
               <div className="space-y-1">
                  <p className="text-xs text-900">{t("workspace.scriptDraft.saveError")}</p>
                  {state.details.map((detail) => (
                     <p key={detail} className="text-xs text-700">
                        {detail}
                     </p>
                  ))}
               </div>
            </div>
         )}

         <div
            className={clsx(
               "rounded-xl border border-200 bg-panel p-3 shadow-lg flex",
               step === "agent" ? "justify-between" : "justify-end"
            )}
         >
            {step === "script" ? (
               <Button compact onClick={() => onStepChange("agent")} iconAfter="arrow-right">
                  {tCommon("next")}
               </Button>
            ) : (
               <>
                  <Button
                     type="tertiary"
                     compact
                     onClick={() => onStepChange("script")}
                     iconBefore="arrow-left"
                  >
                     {tCommon("back")}
                  </Button>
                  <Button
                     compact
                     onClick={() => {
                        if (isVoiceAllowed === false) {
                           onBlockedMissingVoice?.();
                           return;
                        }
                        void save(agentName, direction, voice);
                     }}
                     disabled={
                        state.status === "saving" ||
                        !agentName.trim() ||
                        !voice.id ||
                        isVoiceAllowed === false
                     }
                     iconBefore="floppy-disk"
                  >
                     {state.status === "saving"
                        ? t("workspace.scriptDraft.saving")
                        : t("workspace.scriptDraft.save")}
                  </Button>
               </>
            )}
         </div>
      </div>
   );
};

/**
 * Workspace preview of a script draft proposed by the assistant. The user
 * refines the draft by continuing the conversation, which emits a replacement
 * draft; direction, agent name and voice are edited in place — they pre-fill
 * from the draft contract (or org defaults) and the saved script must be
 * call-ready without a trip through the script editor.
 */
const ScriptDraftView: React.FC<{artifactId: string; draft: ScriptDraft}> = ({
   artifactId,
   draft,
}) => {
   const {t} = useTranslation("ask");
   const {canAccess} = usePermissions();
   const {isGailGptOnlyOrg, org, agentProfile} = useOrg();

   // Interim gpt-only guard: those orgs have no scripts product to save into.
   // Story 12 moves this gate to tool provisioning so the draft is never
   // emitted at all; until then they get a read-only preview.
   const canSave = !isGailGptOnlyOrg() && canAccess(RoleData.Scripts, Permissions.Create);

   // Editable, pre-filled from the draft when the user chose a name in
   // conversation, else like the onboarding script flows: org-configured
   // name, else "Gail". null = untouched, so the default can settle after the
   // org loads async.
   const [agentNameInput, setAgentNameInput] = useState<string | null>(null);
   const agentName =
      agentNameInput ?? (draft.agentName?.trim() || org?.details?.agentName?.trim() || "Gail");

   // null = untouched; pre-fills from the draft contract (the model resolves
   // direction in conversation), falling back to the context type for drafts
   // persisted before the field existed.
   const [directionInput, setDirectionInput] = useState<ScriptDraftDirection | null>(null);
   const direction = directionInput ?? getDraftDirection(draft);

   // null = untouched; defaults to the org's configured voice. Picked cards
   // carry their provider; the default's is inferred from the id, as the
   // onboarding script flows do.
   const [selectedVoice, setSelectedVoice] = useState<ScriptDraftVoice | null>(null);
   const defaultVoiceId = agentProfile.voice || DEFAULT_VOICE_ID;
   const voice = selectedVoice ?? {
      id: defaultVoiceId,
      provider: InferVoiceProviderFromVoiceId(defaultVoiceId),
   };
   const {status: voiceVerifyStatus, isVoiceAllowed} = useVerifyScriptVoice(voice.id);
   const [voicePromptNonce, setVoicePromptNonce] = useState(0);

   useEffect(() => {
      if (isVoiceAllowed !== false) {
         setVoicePromptNonce(0);
      }
   }, [isVoiceAllowed]);

   // Two steps mirroring the script editor: the script content, then the
   // agent (name + voice). Read-only viewers have nothing to set up on the
   // agent step, so they get the content without the wizard.
   const [step, setStep] = useState<DraftStep>("script");
   const showScriptStep = !canSave || step === "script";

   return (
      <div className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">
         {canSave && (
            <Stepper
               steps={[
                  {key: "script", label: t("workspace.scriptDraft.stepScript"), number: 1},
                  {key: "agent", label: t("workspace.scriptDraft.stepAgent"), number: 2},
               ]}
               currentStep={step}
               onStepClick={(key) => setStep(key as DraftStep)}
               compact
               flush
            />
         )}

         {/* Both step bodies stay mounted (toggled with `hidden`, like the
             script editor's sections) so the voice list isn't refetched on
             every step switch and in-progress edits survive navigation. */}
         <div className={clsx("space-y-6", !showScriptStep && "hidden")}>
            {draft.summary && <p className="text-sm text-700">{draft.summary}</p>}

            {canSave && <DirectionSection value={direction} onChange={setDirectionInput} />}

            <SpeechBubble
               content={draft.greeting}
               label={t("workspace.scriptDraft.greeting")}
               icon="comments"
               colorClass="bg-emerald-500"
            />

            {draft.useCases.length > 0 && (
               <div className="space-y-3">
                  <p className="text-sm font-medium text-700">
                     {t("workspace.scriptDraft.actions")}
                  </p>
                  {draft.useCases.map((useCase, index) => (
                     // Use cases have no ids and titles can repeat — title+index keeps keys unique.
                     <UseCaseCard
                        key={`${useCase.title}-${index}`}
                        useCase={useCase}
                        index={index}
                     />
                  ))}
               </div>
            )}

            <SpeechBubble
               content={draft.goodbye}
               label={t("workspace.scriptDraft.goodbye")}
               icon="phone-slash"
               colorClass="bg-blue-500"
            />

            {draft.voicemail && (
               <SpeechBubble
                  content={draft.voicemail}
                  label={t("workspace.scriptDraft.voicemail")}
                  icon="voicemail"
                  colorClass="bg-amber-500"
               />
            )}
         </div>

         {canSave && (
            <div className={clsx("space-y-6", step !== "agent" && "hidden")}>
               <AgentNameSection value={agentName} onChange={setAgentNameInput} />

               <ScriptDraftVoiceSection
                  selectedVoiceId={voice.id}
                  onVoiceSelect={(option) =>
                     setSelectedVoice({id: option.id, provider: option.provider})
                  }
                  orgId={org?.id}
                  voiceVerifyStatus={voiceVerifyStatus}
                  voicePromptNonce={voicePromptNonce}
                  onChooseVoice={() => setStep("agent")}
               />
            </div>
         )}

         {canSave && (
            <ScriptDraftSaveSection
               draft={draft}
               artifactId={artifactId}
               agentName={agentName}
               direction={direction}
               voice={voice}
               step={step}
               onStepChange={setStep}
               isVoiceAllowed={isVoiceAllowed}
               onBlockedMissingVoice={() => {
                  setVoicePromptNonce((n) => n + 1);
                  setStep("agent");
               }}
            />
         )}
      </div>
   );
};

export default ScriptDraftView;
